/**
 * Post Menu — 3-dot context menu for posts.
 * Own posts: share, copy, delete. Others: share, copy, report.
 */

import { memo, useState, useCallback, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  MoreHorizontal, Share2, Copy, Flag, Trash2,
} from 'lucide-react'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'

interface PostMenuProps {
  isOwn: boolean
  onDelete?: () => void
  onReport?: () => void
  onShare?: () => void
}

export const PostMenu = memo(function PostMenu({
  isOwn, onDelete, onReport, onShare,
}: PostMenuProps): React.ReactElement {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)

  const handleOpen = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation()
    hapticImpact('light')
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      const menuWidth = 180
      setPos({
        top: Math.min(rect.bottom + 4, window.innerHeight - 200),
        left: Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8),
      })
    }
    setOpen(true)
  }, [])

  const close = useCallback((): void => setOpen(false), [])

  // Close on scroll
  useEffect(() => {
    if (!open) return
    const handler = (): void => setOpen(false)
    window.addEventListener('scroll', handler, { passive: true, capture: true })
    return () => window.removeEventListener('scroll', handler, { capture: true })
  }, [open])

  const menuAction = useCallback((fn?: () => void) => () => {
    close()
    fn?.()
  }, [close])

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="w-8 h-8 flex items-center justify-center rounded-full text-text-hint/30 hover:text-text-hint/60 hover:bg-white/[0.04] transition-colors tap-highlight-transparent"
      >
        <MoreHorizontal size={18} />
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={close} />
          <div
            className="fixed z-[9999] bg-[var(--color-bg)] border border-white/[0.08] rounded-2xl overflow-hidden min-w-[180px] shadow-2xl py-1"
            style={{ top: pos.top, left: pos.left }}
          >
            <MenuItem icon={Share2} label={t('feed.menu.share')} onClick={menuAction(onShare)} />
            <MenuItem icon={Copy} label={t('feed.menu.copyLink')} onClick={menuAction(() => {
              void navigator.clipboard.writeText(window.location.href)
              hapticImpact('light')
            })} />

            <div className="h-px bg-white/[0.06] mx-3 my-1" />

            {isOwn ? (
              <MenuItem icon={Trash2} label={t('feed.menu.delete')} onClick={menuAction(onDelete)} danger />
            ) : (
              <MenuItem icon={Flag} label={t('feed.menu.report')} onClick={menuAction(onReport)} />
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  )
})

function MenuItem({ icon: Icon, label, onClick, danger }: {
  icon: React.ElementType; label: string; onClick: () => void; danger?: boolean
}): React.ReactElement {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium hover:bg-white/[0.04] transition-colors ${
        danger ? 'text-red-400' : 'text-text'
      }`}
    >
      <Icon size={16} className={danger ? 'text-red-400' : 'text-text-hint/50'} />
      {label}
    </button>
  )
}
